<template>
  <v-app-bar
    flat
    color="transparent"
    class="app-header"
    :class="{ 'is-hidden': headerHidden }"
  >
    <div class="header-inner content-shell">
      <button
        type="button"
        class="brand"
        :aria-label="t('app.brand')"
        @click="emit('navigate-home')"
      >
        <img :src="logoSrc" :alt="t('app.brand')" class="brand-logo" />
      </button>
      <div class="header-actions">
        <div class="auth-block">
          <v-btn
            icon
            size="small"
            variant="text"
            class="theme-toggle"
            :aria-label="themeLabel"
            @click="toggleTheme"
          >
            <v-icon :icon="themeIcon" />
          </v-btn>
          <v-menu>
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                variant="text"
                prepend-icon="mdi-earth"
                class="lang-btn"
              >
                {{ localeFullLabel }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="lang in languages"
                :key="lang.code"
                :title="lang.label"
                @click="changeLocale(lang.code)"
              />
            </v-list>
          </v-menu>
          <v-btn
            v-if="isAnonymous"
            color="primary"
            rounded="pill"
            append-icon="mdi-login"
            @click="emit('sign-in')"
          >
            {{ t('auth.signInGoogle') }}
          </v-btn>
          <v-menu v-else>
            <template #activator="{ props: userProps }">
              <v-btn
                v-bind="userProps"
                variant="tonal"
                rounded="pill"
                class="user-menu-btn"
                append-icon="mdi-chevron-down"
              >
                <v-avatar size="28" class="avatar avatar-button">
                  <LazyImage
                    v-if="avatarUrl"
                    :src="avatarUrl"
                    alt="User avatar"
                  />
                  <span v-else>{{ avatarInitial }}</span>
                </v-avatar>
                {{ displayLabel }}
              </v-btn>
            </template>
            <v-list class="user-menu">
              <v-list-item>
                <template #prepend>
                  <v-avatar size="32" class="avatar">
                    <LazyImage
                      v-if="avatarUrl"
                      :src="avatarUrl"
                      alt="User avatar"
                    />
                    <span v-else>{{ avatarInitial }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ displayLabel }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="isAdmin"
                title="Admin"
                append-icon="mdi-security"
                @click="handleNavigateAdmin"
              />
              <v-list-item
                density="comfortable"
                :title="t('history.title')"
                @click="handleNavigate('history')"
              />
              <v-list-item
                :title="t('saved.title')"
                @click="handleNavigate('saved')"
              />

              <v-list-item :title="planLabel" @click="emit('buy-credits')">
                <template #append>
                  <v-chip class="credits-pill" color="primary" variant="tonal">
                    {{ credits }}
                  </v-chip>
                </template>
              </v-list-item>
              <v-divider />
              <v-list-item
                :title="t('auth.signOut')"
                @click="emit('sign-out')"
                append-icon="mdi-logout"
              />
            </v-list>
          </v-menu>
        </div>
      </div>
      <v-app-bar-nav-icon
        class="mobile-only"
        :icon="drawer ? 'mdi-close' : 'mdi-menu'"
        :aria-label="drawer ? t('nav.closeMenu') : t('nav.openMenu')"
        @click.stop="drawer = !drawer"
      />
    </div>
  </v-app-bar>

  <v-navigation-drawer
    v-model="drawer"
    location="right"
    temporary
    class="mobile-drawer"
  >
    <div class="mobile-drawer__head">
      <v-menu>
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            variant="text"
            prepend-icon="mdi-earth"
            append-icon="mdi-chevron-down"
            class="lang-btn"
          >
            {{ localeFullLabel }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="lang in languages"
            :key="lang.code"
            :title="lang.label"
            @click="changeLocale(lang.code)"
          />
        </v-list>
      </v-menu>
      <v-btn
        icon
        size="small"
        variant="text"
        class="theme-toggle"
        :aria-label="themeLabel"
        @click="toggleTheme"
      >
        <v-icon :icon="themeIcon" />
      </v-btn>
    </div>
    <v-divider />

    <div class="drawer-auth">
      <v-btn
        v-if="isAnonymous"
        color="primary"
        rounded="pill"
        append-icon="mdi-login"
        block
        @click="emit('sign-in')"
      >
        {{ t('auth.signInGoogle') }}
      </v-btn>
      <template v-else>
        <div class="drawer-user">
          <v-avatar size="36" class="avatar">
            <LazyImage v-if="avatarUrl" :src="avatarUrl" alt="User avatar" />
            <span v-else>{{ avatarInitial }}</span>
          </v-avatar>
          <span>{{ displayLabel }}</span>
        </div>
        <v-chip
          v-if="credits !== null"
          size="medium"
          variant="tonal"
          density="comfortable"
          class="credits-chip"
        >
          {{ t('credits.balanceShort', { count: credits }) }}
        </v-chip>
        <v-divider />

        <v-list v-if="showNav" nav>
          <v-list-item
            v-if="isAdmin"
            class="px-0"
            title="Admin"
            append-icon="mdi-security"
            @click="handleNavigateAdmin"
          />
          <v-list-item
            class="px-0"
            :title="t('history.title')"
            @click="handleNavigate('history')"
          />
          <v-list-item
            class="px-0"
            :title="t('saved.title')"
            @click="handleNavigate('saved')"
          />
        </v-list>

        <v-btn
          variant="elevated"
          color="primary"
          rounded="pill"
          block
          @click="emit('buy-credits')"
        >
          {{ planLabel }}
        </v-btn>
      </template>
    </div>
    <template v-slot:append>
      <v-btn
        v-if="showNav"
        variant="text"
        prepend-icon="mdi-logout"
        block
        class="my-4"
        @click="emit('sign-out')"
      >
        {{ t('auth.signOut') }}
      </v-btn>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay, useTheme } from 'vuetify';

import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../i18n';
import LazyImage from '../ui/LazyImage.vue';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import logoDark from '../../images/logo/logo-dark.png';
import logoLight from '../../images/logo/logo-light.png';

const props = defineProps<{
  userLabel: string | null;
  isAnonymous: boolean;
  credits: number | null;
  avatarUrl: string | null;
  planId?: 'free' | 'basic' | 'standard' | 'enterprise' | null;
  isAdmin?: boolean;
}>();

const { t, locale } = useI18n();
const theme = useTheme();
const { smAndDown } = useDisplay();

const emit = defineEmits([
  'sign-in',
  'sign-out',
  'buy-credits',
  'navigate-history',
  'navigate-saved',
  'navigate-admin',
  'navigate-home',
]);

const drawer = ref(false);
const headerHidden = ref(false);
let lastScrollY = 0;

function handleScroll() {
  if (!smAndDown.value) {
    headerHidden.value = false;
    lastScrollY = window.scrollY;
    return;
  }
  const current = window.scrollY;
  if (current <= 64) {
    headerHidden.value = false;
  } else if (current > lastScrollY) {
    headerHidden.value = true;
    drawer.value = false;
  } else if (current < lastScrollY) {
    headerHidden.value = false;
  }
  lastScrollY = current;
}

const displayLabel = computed(() => props.userLabel || t('auth.signedIn'));
const avatarInitial = computed(() =>
  displayLabel.value.charAt(0).toUpperCase(),
);
const showNav = computed(() => !props.isAnonymous);
const isAdmin = computed(() => props.isAdmin === true);
const planLabel = computed(() => {
  const planId = props.planId ?? 'free';
  if (!planId || planId === 'free') {
    return t('credits.buyCredits');
  }
  return t(`credits.packages.${planId}`);
});
const languages = computed(() =>
  SUPPORTED_LOCALES.map((code) => ({
    code,
    label: t(`language.${code}`),
  })),
);
const localeFullLabel = computed(
  () => languages.value.find((item) => item.code === locale.value)?.label ?? '',
);

const isDark = computed(() => theme.global.name.value === 'darkTheme');
const logoSrc = computed(() => (isDark.value ? logoDark : logoLight));
const themeIcon = computed(() =>
  isDark.value ? 'mdi-white-balance-sunny' : 'mdi-weather-night',
);
const themeLabel = computed(() =>
  isDark.value ? t('theme.light') : t('theme.dark'),
);

function changeLocale(code: SupportedLocale) {
  setLocale(code);
  drawer.value = false;
}

function applyTheme(name: 'darkTheme' | 'lightTheme') {
  theme.change(name);
  localStorage.setItem('theme', name);
}

function toggleTheme() {
  applyTheme(isDark.value ? 'lightTheme' : 'darkTheme');
}

function handleNavigate(target: 'history' | 'saved') {
  if (target === 'history') {
    emit('navigate-history');
  } else {
    emit('navigate-saved');
  }
  drawer.value = false;
}

function handleNavigateAdmin() {
  emit('navigate-admin');
  drawer.value = false;
}

useBodyScrollLock(drawer);

onMounted(() => {
  const saved = localStorage.getItem('theme');
  if (saved === 'darkTheme' || saved === 'lightTheme') {
    theme.change(saved);
  }
  lastScrollY = window.scrollY;
  window.addEventListener('scroll', handleScroll, { passive: true });
});

watch(smAndDown, (value) => {
  if (!value) {
    headerHidden.value = false;
  }
});

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped lang="scss">
.app-header {
  backdrop-filter: blur(12px);
  transition: transform 0.2s ease;
  will-change: transform;
}

.app-header.is-hidden {
  transform: translateY(-100%) !important;
  pointer-events: none;
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.brand-logo {
  width: auto;
  height: 2rem;
  object-fit: contain;
  display: block;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.auth-block {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: 0.6rem;
}

.theme-toggle {
  color: var(--app-muted);
}

.lang-btn {
  text-transform: uppercase;
  color: var(--app-muted);
}

.user-chip {
  background: var(--chip-bg);
  color: var(--chip-text);
}

.credits-chip {
  padding: 0 0.5rem;
  margin: 0.5rem 0 1rem;
  background: var(--chip-bg);
  color: var(--chip-text);
}

.user-menu-btn {
  text-transform: none;
}

.user-menu {
  min-width: 220px;
}

.avatar {
  background: var(--avatar-bg);
  color: var(--app-text);
  font-weight: 600;
}

.user-menu-btn :deep(.v-btn__content) {
  gap: 0.5rem;
  padding-left: 0;
}

.avatar-button {
  margin-left: -0.5rem;
  margin-top: -0.25rem;
  margin-bottom: -0.25rem;
}

.drawer-user {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.2rem;
}

.credits-item {
  display: flex;
  justify-content: center;
  padding: 0.5rem;
}

.credits-pill {
  font-weight: 600;
  letter-spacing: 0.02em;
}

.nav-link {
  text-transform: none;
  color: var(--app-muted);
}

@media (max-width: 900px) {
  .header-actions {
    display: none;
  }
}

.mobile-only {
  display: inline-flex;
  @media (min-width: 901px) {
    display: none;
  }
}

.mobile-drawer {
  background: var(--drawer-bg);
  color: var(--app-text);
  backdrop-filter: blur(12px);
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1rem 0.6rem;
}

.drawer-auth {
  padding: 1rem;
}

.drawer-languages {
  display: grid;
  gap: 0.3rem;
  margin-bottom: 0.8rem;
}
</style>
